import { Repeat, Video, Handshake } from 'lucide-react';
import { RealScreen } from '@/components/marketing/roleKit';

/**
 * Tutors Real Screen 2: scheduling. Student-proposed times need your action (accept or counter);
 * recurring series are approved, approved with exceptions, or declined by the tutor.
 */
function Pill({ children, solid }: { children: React.ReactNode; solid?: boolean }) {
  return (
    <span
      aria-hidden
      className={`inline-flex h-8 items-center rounded-md px-3 text-xs font-medium ${solid ? 'bg-sky-800 text-white dark:bg-sky-500 dark:text-sky-950' : 'border'}`}
    >
      {children}
    </span>
  );
}

export function TutorSchedulingSection() {
  return (
    <RealScreen
      id="section-tutor-scheduling"
      audience="tutors"
      layout="rail-right"
      heading="Agree times in the app, not in a thread"
      lede="Requests reach you with a proposed time. Accept it, or counter with yours."
      callouts={[
        { icon: Handshake, title: 'Accept or counter', body: 'Both of you confirm before a session is scheduled.' },
        { icon: Repeat, title: 'Recurring series are your call', body: 'Approve a weekly series, approve it with exceptions, or decline it.' },
        { icon: Video, title: 'Meeting links are made for you', body: 'Each scheduled session gets a Google Meet link and a calendar entry.' },
      ]}
      caption="Example tutor sessions list."
    >
      <div className="space-y-3">
        <div className="rounded-xl border bg-background p-4">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div>
              <p className="text-sm font-semibold">Alex M. · Algebra</p>
              <p className="text-xs text-muted-foreground">Wed 4:00 PM · 60 min</p>
            </div>
            <span className="rounded-full bg-destructive/10 px-2 py-0.5 text-xs font-medium text-destructive">Student proposed</span>
          </div>
          <div className="mt-3 flex flex-wrap gap-2">
            <Pill solid>Accept</Pill>
            <Pill>Counter-propose</Pill>
            <Pill>Message</Pill>
          </div>
        </div>
        <div className="rounded-xl border bg-background p-4">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div>
              <p className="text-sm font-semibold">Jamie L. · SAT Math</p>
              <p className="text-xs text-muted-foreground">Weekly, Fridays 3:00 PM · 8 sessions</p>
            </div>
            <span className="rounded-full bg-amber-500/15 px-2 py-0.5 text-xs font-medium text-amber-700 dark:text-amber-400">Awaiting approval</span>
          </div>
          <div className="mt-3 flex flex-wrap gap-2">
            <Pill solid>Approve</Pill>
            <Pill>Approve with exceptions</Pill>
            <Pill>Decline</Pill>
          </div>
        </div>
      </div>
    </RealScreen>
  );
}
